/**
 * شاشة «الجروبات وحزمة التهيئة» — `/groups` (docs/03 §11).
 *
 * الشاشة دي بتفك آخر وقفة في رحلة الطالب الجديد: خطوة ٨ «إنشاء الجروب
 * وحزمة التهيئة» — شرط إقفالها وجود جروب فعلي، ومفيش شاشة كانت بتعمل جروب.
 *
 * 🔴 **جروب إشعارات افتراضي واحد بس لكل أسرة.** ده اللي بيمنع «إشعارات
 *    إخوتهم تروح على بعض». الشاشة بتعرض التحذير الجاي من السيستم لما
 *    الأسرة يبقى عندها أكتر من جروب وما اختارتش، وزرار الاختيار جنبه.
 *
 * 🔑 **جروب المذاكرة لكل (طالب × مادة)** — لازم يتربط باشتراك. الشاشة
 *    بتجيب اشتراكات الأسرة وبتخلّيك تختار، وما بتخترعش مادة.
 *
 * ⚠️ **سؤال مفتوح للمؤسس:** الجروب قبل الترشيح ولا بعده؟ القالب المنشور
 *    حاطه بعد الترشيح (خطوة ٨) — الشاشة شغّالة في الحالتين.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Shell } from "@/components/Shell";
import {
  EmptyState,
  ErrorState,
  LoadingState,
  OfflineState,
} from "@/components/states/ScreenStates";
import { Ltr, fmtNum } from "@/lib/format";
import { errorMessageAr, OfflineError } from "@/api/client";
import { useRouter } from "@/lib/router";
import {
  GROUP_TYPES,
  PLATFORMS,
  archiveGroup,
  createGroup,
  fetchGroups,
  fetchPendingGroupWork,
  setChecklistItem,
  setDefaultNotificationGroup,
  type GroupRow,
  type GroupWarning,
  type PendingGroupWork,
} from "@/api/groups";
import { fetchEnrollments, type EnrollmentRow } from "@/api/evaluation";

type Load =
  | { kind: "loading" }
  | { kind: "ready" }
  | { kind: "error"; messageAr: string }
  | { kind: "offline" };

export function Groups() {
  const { param, navigate } = useRouter();
  const familyId = param("family");

  const [state, setState] = useState<Load>({ kind: "loading" });
  const [pending, setPending] = useState<PendingGroupWork[]>([]);
  const [groups, setGroups] = useState<GroupRow[]>([]);
  const [warnings, setWarnings] = useState<GroupWarning[]>([]);
  const [enrollments, setEnrollments] = useState<EnrollmentRow[]>([]);
  const [toast, setToast] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setState({ kind: "loading" });
    try {
      const work = await fetchPendingGroupWork();
      setPending(work);
      if (familyId) {
        const [g, enr] = await Promise.all([
          fetchGroups(familyId),
          fetchEnrollments({ familyId }),
        ]);
        setGroups(g.rows);
        setWarnings(g.warnings);
        setEnrollments(enr);
      } else {
        setGroups([]);
        setWarnings([]);
        setEnrollments([]);
      }
      setState({ kind: "ready" });
    } catch (err) {
      // ⛔ ممنوع الفشل الصامت
      console.error("[groups] تحميل الشاشة فشل", err);
      setState(
        err instanceof OfflineError
          ? { kind: "offline" }
          : { kind: "error", messageAr: errorMessageAr(err) },
      );
    }
  }, [familyId]);

  useEffect(() => {
    void load();
  }, [load]);

  const familyName = useMemo(
    () => pending.find((w) => w.family.id === familyId)?.family.nameAr ?? null,
    [pending, familyId],
  );

  async function run(key: string, fn: () => Promise<string>) {
    setBusy(key);
    try {
      setToast(await fn());
      await load();
    } catch (err) {
      console.error("[groups] الفعل فشل", err);
      setToast(errorMessageAr(err));
    } finally {
      setBusy(null);
    }
  }

  const crumb = familyId ? (
    <>
      جروبات <bdi>{familyName ?? "الأسرة"}</bdi>
    </>
  ) : (
    <>
      <span className="num">{fmtNum(pending.length)}</span> اشتراك مستني جروب
    </>
  );

  return (
    <Shell title="الجروبات وحزمة التهيئة" crumb={crumb}>
      {toast ? (
        <div className="chip okc" role="status">
          <span className="bullet" />
          {toast}
        </div>
      ) : null}

      {state.kind === "loading" ? <LoadingState rows={4} /> : null}
      {state.kind === "offline" ? <OfflineState onRetry={() => void load()} /> : null}
      {state.kind === "error" ? (
        <ErrorState messageAr={state.messageAr} onRetry={() => void load()} />
      ) : null}

      {state.kind === "ready" ? (
        <>
          {/* ═══ (1) اشتراكات مستنية جروب — مصدرها الرحلات نفسها ═══ */}
          <div className="card" style={{ overflow: "hidden" }}>
            <div className="sec-h" style={{ padding: "14px 16px 10px" }}>
              <span className="sec-title">
                اشتراكات مستنية جروب <span className="num">{fmtNum(pending.length)}</span>
              </span>
              <span className="t-meta">
                المصدر: بنود خطوة «إنشاء الجروب» المفتوحة في الرحلات الشغالة
              </span>
            </div>
            {pending.length === 0 ? (
              <EmptyState
                title="مفيش اشتراك مستني جروب"
                hint="الاشتراك بيوصل هنا بعد ما الكورس يتفعّل في الرحلة"
              />
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>الاشتراك</th>
                      <th>الأسرة</th>
                      <th>الإجراء</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pending.map((w, i) => (
                      <tr key={w.enrollment.id} className="row-link">
                        <td className="t-body">
                          {w.enrollment.labelAr || (
                            <span className="missing">اشتراك بلا اسم</span>
                          )}
                        </td>
                        <td className="t-desc">{w.family.nameAr}</td>
                        <td>
                          <button
                            type="button"
                            /* ⛔ زرار أساسي واحد في الشاشة — أول صف بس */
                            className={`btn sm ${
                              i === 0 && !familyId ? "primary" : "ghost"
                            }`}
                            onClick={() => navigate(`/groups?family=${w.family.id}`)}
                          >
                            افتح جروبات الأسرة
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* ═══ (2) جروبات الأسرة المختارة ═══ */}
          {familyId ? (
            <>
              {warnings.map((w) => (
                <div key={w.code} className="chip soon" role="alert">
                  <span className="bullet" />
                  {w.messageAr}
                </div>
              ))}

              <NewGroupCard
                familyId={familyId}
                enrollments={enrollments}
                busy={busy !== null}
                onDone={(msg) => void run("create", async () => msg)}
              />

              <div className="card" style={{ overflow: "hidden" }}>
                <div className="sec-h" style={{ padding: "14px 16px 10px" }}>
                  <span className="sec-title">
                    جروبات الأسرة <span className="num">{fmtNum(groups.length)}</span>
                  </span>
                  <span className="t-meta">
                    حزمة التهيئة سبع بنود — الخطوة ما بتتقفلش قبل ما تكمل
                  </span>
                </div>

                {groups.length === 0 ? (
                  <EmptyState
                    title="الأسرة دي لسه مالهاش جروبات"
                    hint="اعمل جروب المذاكرة لكل اشتراك"
                  />
                ) : (
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    {groups.map((g) => (
                      <div
                        key={g.id}
                        style={{ borderTop: "1px solid var(--line)", padding: "12px 16px" }}
                      >
                        <div
                          style={{
                            display: "flex",
                            gap: 10,
                            alignItems: "center",
                            flexWrap: "wrap",
                          }}
                        >
                          <strong className="t-body">
                            <bdi>{g.nameAr}</bdi>
                          </strong>
                          <span className="chip ghost">{g.typeLabelAr}</span>
                          <span className="chip ghost">{g.platformLabelAr}</span>
                          {g.isNotificationDefault ? (
                            <span className="chip info">
                              <span className="bullet" />
                              جروب الإشعارات الافتراضي
                            </span>
                          ) : null}
                          <span
                            className={`chip ${g.checklistComplete ? "okc" : "soon"}`}
                          >
                            <span className="bullet" />
                            حزمة التهيئة <span className="num">{fmtNum(g.checklistDone)}</span>
                            {"/"}
                            <span className="num">{fmtNum(g.checklistTotal)}</span>
                          </span>
                          {g.externalRef ? (
                            <span className="t-meta">
                              <Ltr>{g.externalRef}</Ltr>
                            </span>
                          ) : (
                            <span className="t-meta missing">مفيش لينك مسجّل</span>
                          )}
                        </div>

                        <div
                          style={{
                            display: "flex",
                            gap: 6,
                            flexWrap: "wrap",
                            marginTop: 8,
                          }}
                        >
                          {g.checklist.map((c) => (
                            <button
                              key={c.key}
                              type="button"
                              className={`filt${c.done ? " on" : ""}`}
                              aria-pressed={c.done}
                              disabled={busy !== null || g.status === "archived"}
                              onClick={() =>
                                void run(`${g.id}:${c.key}`, async () => {
                                  const out = await setChecklistItem(g.id, c.key, !c.done);
                                  return out.toastAr;
                                })
                              }
                            >
                              {c.labelAr}
                            </button>
                          ))}
                        </div>

                        <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                          {!g.isNotificationDefault && g.status !== "archived" ? (
                            <button
                              type="button"
                              className="btn sm ghost"
                              disabled={busy !== null}
                              onClick={() =>
                                void run(`${g.id}:default`, async () => {
                                  const out = await setDefaultNotificationGroup(g.id);
                                  return out.toastAr;
                                })
                              }
                            >
                              خلّيه جروب الإشعارات
                            </button>
                          ) : null}
                          {g.status !== "archived" ? (
                            <button
                              type="button"
                              className="btn sm ghost"
                              disabled={busy !== null}
                              onClick={() => {
                                const reason = window.prompt("سبب الأرشفة؟");
                                if (!reason || reason.trim().length < 2) return;
                                void run(`${g.id}:archive`, async () => {
                                  const out = await archiveGroup(g.id, reason.trim());
                                  return out.toastAr;
                                });
                              }}
                            >
                              أرشفة
                            </button>
                          ) : (
                            <span className="t-meta">مؤرشف</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          ) : null}
        </>
      ) : null}
    </Shell>
  );
}

/* ══════════════════ فورم جروب جديد ══════════════════ */

function NewGroupCard({
  familyId,
  enrollments,
  busy,
  onDone,
}: {
  familyId: string;
  enrollments: EnrollmentRow[];
  busy: boolean;
  /** بيعرض الرسالة **وبيعيد التحميل** — القايمة لازم تعكس الجروب الجديد */
  onDone: (msg: string) => void;
}) {
  const [type, setType] = useState("study");
  const [enrollmentId, setEnrollmentId] = useState(enrollments[0]?.id ?? "");
  const [name, setName] = useState("");
  const [platform, setPlatform] = useState("whatsapp");
  const [link, setLink] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [working, setWorking] = useState(false);

  useEffect(() => {
    if (!enrollmentId && enrollments.length > 0) setEnrollmentId(enrollments[0]!.id);
  }, [enrollments, enrollmentId]);

  const chosen = enrollments.find((e) => e.id === enrollmentId) ?? null;
  const suggested = chosen ? `رتّل — ${chosen.labelAr}` : "";

  async function submit() {
    const label = (name.trim() || suggested).trim();
    if (label.length < 2) {
      setError("اكتب اسم الجروب");
      return;
    }
    if (type === "study" && !enrollmentId) {
      setError("جروب المذاكرة لازم يتربط باشتراك (طالب × مادة)");
      return;
    }
    setWorking(true);
    setError(null);
    try {
      const out = await createGroup({
        familyId,
        nameAr: label,
        type,
        enrollmentId: type === "study" ? enrollmentId : null,
        studentId: type === "study" ? chosen?.studentId ?? null : null,
        platform,
        externalRef: link.trim() || null,
      });
      setName("");
      setLink("");
      onDone(out.toastAr);
    } catch (err) {
      console.error("[groups] إنشاء الجروب فشل", err);
      setError(errorMessageAr(err));
    } finally {
      setWorking(false);
    }
  }

  return (
    <div className="card" style={{ padding: 16, display: "flex", flexDirection: "column", gap: 12 }}>
      <div className="sec-h">
        <span className="sec-title">جروب جديد</span>
        <span className="t-meta">
          جروب المذاكرة لكل (طالب × مادة) — أسرة بولدين ومادتين = أربع جروبات
        </span>
      </div>

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span className="t-meta">النوع</span>
          <select className="field" value={type} onChange={(e) => setType(e.target.value)}>
            {GROUP_TYPES.map((t) => (
              <option key={t.key} value={t.key}>
                {t.labelAr}
              </option>
            ))}
          </select>
        </label>

        {type === "study" ? (
          <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span className="t-meta">الاشتراك</span>
            <select
              className="field"
              value={enrollmentId}
              onChange={(e) => setEnrollmentId(e.target.value)}
            >
              {enrollments.length === 0 ? (
                <option value="">مفيش اشتراكات للأسرة دي</option>
              ) : null}
              {enrollments.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.labelAr || e.subjectLabelAr}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        <label style={{ display: "flex", flexDirection: "column", gap: 4, flex: "1 1 220px" }}>
          <span className="t-meta">اسم الجروب</span>
          <input
            className="field"
            value={name}
            placeholder={suggested || "اسم الجروب"}
            onChange={(e) => setName(e.target.value)}
          />
        </label>

        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span className="t-meta">المنصّة</span>
          <select
            className="field"
            value={platform}
            onChange={(e) => setPlatform(e.target.value)}
          >
            {PLATFORMS.map((pl) => (
              <option key={pl.key} value={pl.key}>
                {pl.labelAr}
              </option>
            ))}
          </select>
        </label>

        <label style={{ display: "flex", flexDirection: "column", gap: 4, flex: "1 1 220px" }}>
          <span className="t-meta">لينك الدعوة (اختياري)</span>
          <input
            className="field"
            value={link}
            onChange={(e) => setLink(e.target.value)}
            placeholder="https://…"
          />
        </label>
      </div>

      {error ? (
        <div className="t-meta" style={{ color: "var(--danger)" }} role="alert">
          {error}
        </div>
      ) : null}

      <div>
        <button
          type="button"
          className={`btn primary${working ? " is-busy" : ""}`}
          disabled={busy || working || (type === "study" && enrollments.length === 0)}
          onClick={() => void submit()}
        >
          {working ? "بيتعمل…" : "اعمل الجروب"}
        </button>
      </div>
    </div>
  );
}
